import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Check } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { PriorityIcon, PriorityPill } from './PriorityIcon';
import { WorkTypeIcon } from './KanbanBoard';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { toast } from 'sonner';

const STATUS_LABELS = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
const STATUS_COLORS = {
  todo: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  review: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Subtasks panel rendered inside a Story or Bug detail view. Mirrors Jira's
// Subtasks panel: progress bar (% done), inline-add input, and a small
// editable table of children. Each row is a real task with work_type='task'
// linked to this parent via parent_task_id.
export default function SubtasksSection({ parentTask, projectId, allTasks, members, onCreated, onTaskClick, canEdit }) {
  const { getOrgHeaders, API } = useAuth();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const subtasks = useMemo(() =>
    (allTasks || []).filter(t => t.parent_task_id === parentTask?.task_id && !t.is_deleted),
    [allTasks, parentTask?.task_id]
  );

  const doneCount = subtasks.filter(t => t.status === 'done').length;
  const pct = subtasks.length === 0 ? 0 : Math.round((doneCount / subtasks.length) * 100);

  const submitNew = async () => {
    const title = draft.trim();
    if (!title) return;
    setSubmitting(true);
    try {
      // IMPORTANT: pass `getOrgHeaders()` UNDER a `headers` key + always
      // set `withCredentials: true`. Passing `getOrgHeaders()` directly as
      // the axios config object made axios treat the bag as a config (not
      // as headers) AND omitted the credentials flag — which causes
      // "Not authenticated" 401s the moment the front-end is served from
      // a different origin than the backend (common on production VPS
      // deployments). Same-origin previews mask this by sending the
      // session cookie automatically.
      await axios.post(`${API}/projects/${projectId}/tasks`, {
        title,
        parent_task_id: parentTask.task_id,
        // Status / priority intentionally use defaults — power users can edit
        // inline after creation.
      }, { withCredentials: true, headers: getOrgHeaders() });
      setDraft('');
      setAdding(false);
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add subtask');
    } finally { setSubmitting(false); }
  };

  const updateField = async (taskId, patch) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, patch, { withCredentials: true, headers: getOrgHeaders() });
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white" data-testid="subtasks-section">
      <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/60 flex items-center gap-3">
        <span className="text-[12px] font-bold text-zinc-700 tracking-wide">Subtasks</span>
        {subtasks.length > 0 && (
          <>
            <div className="flex-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] text-zinc-500 font-medium">{pct}% Done</span>
          </>
        )}
        {!subtasks.length && <div className="flex-1" />}
        {canEdit && !adding && (
          <button onClick={() => setAdding(true)} data-testid="add-subtask-btn"
            className="text-[11px] font-semibold text-zinc-500 hover:text-zinc-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-200/60">
            <Plus size={12} weight="bold" /> Add
          </button>
        )}
      </div>

      {subtasks.length === 0 && !adding && (
        <div className="px-4 py-5 text-center text-[12px] text-zinc-400">
          No subtasks yet. Break this {parentTask?.work_type || 'item'} into smaller tasks.
        </div>
      )}

      {subtasks.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50/40">
                <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold w-7"></th>
                <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold w-24">Key</th>
                <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold">Title</th>
                <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold w-24">Priority</th>
                <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold w-32">Assignee</th>
                <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {subtasks.map(t => {
                const assignee = members?.find(m => m.user_id === t.assignee_id);
                const aStyle = assignee ? getAvatarStyle(assignee.user_id || assignee.name) : null;
                const isDone = t.status === 'done';
                return (
                  <tr key={t.task_id} className="border-b border-zinc-50 hover:bg-zinc-50/60 cursor-pointer transition-colors" data-testid={`subtask-row-${t.task_id}`}
                    onClick={() => onTaskClick && onTaskClick(t)}>
                    <td className="px-3 py-1.5 text-center"><WorkTypeIcon type="task" size={13} /></td>
                    <td className={`px-2 py-1.5 text-[11px] font-mono ${isDone ? 'line-through text-zinc-300' : 'text-zinc-400'}`}>{t.task_key}</td>
                    <td className={`px-2 py-1.5 text-[12px] ${isDone ? 'line-through text-zinc-400' : 'text-zinc-800'}`}>{t.title}</td>
                    <td className="px-2 py-1.5">
                      {canEdit ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button onClick={(e) => e.stopPropagation()} data-testid={`subtask-priority-${t.task_id}`} className="hover:opacity-70">
                              {t.priority ? <PriorityPill priority={t.priority} /> : <span className="text-[11px] text-zinc-400">Set</span>}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-36 p-1" onClick={(e) => e.stopPropagation()}>
                            {PRIORITIES.map(p => (
                              <button key={p.value} type="button"
                                onClick={(e) => { e.stopPropagation(); updateField(t.task_id, { priority: p.value }); }}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-700 hover:bg-zinc-50">
                                <PriorityIcon priority={p.value} size={12} />
                                <span className="flex-1 text-left">{p.label}</span>
                                {t.priority === p.value && <Check size={11} className="text-blue-600" />}
                              </button>
                            ))}
                          </PopoverContent>
                        </Popover>
                      ) : (t.priority ? <PriorityPill priority={t.priority} /> : <span className="text-[11px] text-zinc-400">—</span>)}
                    </td>
                    <td className="px-2 py-1.5">
                      {canEdit ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button onClick={(e) => e.stopPropagation()} data-testid={`subtask-assignee-${t.task_id}`} className="hover:opacity-80 transition-opacity">
                              {assignee ? (
                                <div className="flex items-center gap-1.5">
                                  <Avatar className="h-5 w-5"><AvatarImage src={assignee.avatar} /><AvatarFallback className={`${aStyle.bg} text-white text-[8px] font-bold`}>{getInitials(assignee.name)}</AvatarFallback></Avatar>
                                  <span className="text-[11px] text-zinc-600 truncate max-w-[100px]">{assignee.name}</span>
                                </div>
                              ) : <span className="text-[11px] text-zinc-400 hover:text-zinc-700">Unassigned</span>}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-1" onClick={(e) => e.stopPropagation()}>
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); if (t.assignee_id) updateField(t.task_id, { assignee_id: null }); }}
                              data-testid={`subtask-assignee-unassigned-${t.task_id}`}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-600 hover:bg-zinc-50">
                              <span className="w-5 h-5 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-400 text-[10px]">—</span>
                              <span className="flex-1 text-left">Unassigned</span>
                              {!t.assignee_id && <Check size={11} className="text-blue-600" />}
                            </button>
                            <div className="max-h-44 overflow-y-auto">
                              {(members || []).map(m => {
                                const s = getAvatarStyle(m.user_id || m.name);
                                return (
                                  <button key={m.user_id} type="button"
                                    onClick={(e) => { e.stopPropagation(); if (m.user_id !== t.assignee_id) updateField(t.task_id, { assignee_id: m.user_id }); }}
                                    data-testid={`subtask-assignee-option-${t.task_id}-${m.user_id}`}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-700 hover:bg-zinc-50">
                                    <Avatar className="h-5 w-5"><AvatarImage src={m.avatar} /><AvatarFallback className={`${s.bg} text-white text-[8px] font-bold`}>{getInitials(m.name)}</AvatarFallback></Avatar>
                                    <span className="flex-1 text-left truncate">{m.name}</span>
                                    {m.user_id === t.assignee_id && <Check size={11} className="text-blue-600" />}
                                  </button>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : assignee ? (
                        <div className="flex items-center gap-1.5">
                          <Avatar className="h-5 w-5"><AvatarImage src={assignee.avatar} /><AvatarFallback className={`${aStyle.bg} text-white text-[8px] font-bold`}>{getInitials(assignee.name)}</AvatarFallback></Avatar>
                          <span className="text-[11px] text-zinc-600 truncate">{assignee.name}</span>
                        </div>
                      ) : <span className="text-[11px] text-zinc-400">Unassigned</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {canEdit ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button onClick={(e) => e.stopPropagation()} data-testid={`subtask-status-${t.task_id}`}
                              className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${STATUS_COLORS[t.status] || 'bg-zinc-100 text-zinc-700 border-zinc-200'} hover:opacity-80`}>
                              {STATUS_LABELS[t.status] || t.status}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-40 p-1" onClick={(e) => e.stopPropagation()}>
                            {Object.keys(STATUS_LABELS).map(s => (
                              <button key={s} type="button"
                                onClick={(e) => { e.stopPropagation(); updateField(t.task_id, { status: s }); }}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-700 hover:bg-zinc-50">
                                <span className="flex-1 text-left">{STATUS_LABELS[s]}</span>
                                {t.status === s && <Check size={11} className="text-blue-600" />}
                              </button>
                            ))}
                          </PopoverContent>
                        </Popover>
                      ) : <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${STATUS_COLORS[t.status] || 'bg-zinc-100 text-zinc-700 border-zinc-200'}`}>{STATUS_LABELS[t.status] || t.status}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && canEdit && (
        <div className="px-3 py-2 border-t border-zinc-100 bg-zinc-50/30 flex items-center gap-2" data-testid="subtask-add-row">
          <WorkTypeIcon type="task" size={13} />
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What needs to be done?"
            disabled={submitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitNew();
              if (e.key === 'Escape') { setDraft(''); setAdding(false); }
            }}
            className="h-7 text-[12px] flex-1"
            data-testid="subtask-input"
          />
          <button onClick={submitNew} disabled={!draft.trim() || submitting}
            data-testid="subtask-save-btn"
            className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 disabled:text-zinc-300 px-2 py-1">
            {submitting ? 'Adding…' : 'Add'}
          </button>
          <button onClick={() => { setDraft(''); setAdding(false); }}
            className="text-[11px] font-semibold text-zinc-400 hover:text-zinc-600 px-2 py-1">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
